import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const SlideContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 1rem;
  padding-top: 4.5rem;
  max-width: 1200px;
  margin: 0 auto;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 0.8rem;
    padding-top: 4rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    padding-top: 3.5rem;
  }
`;

const SlideTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 2rem;
  background: linear-gradient(45deg, #6F1A3F, #C82B3F);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 1024px) {
    font-size: 3rem;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  width: 100%;
`;

const ServiceCard = styled(motion.div)`
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(111, 26, 63, 0.3);
  border-radius: 15px;
  padding: 2rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: rgba(200, 43, 63, 0.6);
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(111, 26, 63, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(45deg, #6F1A3F, #C82B3F);
  }
`;

const ServiceIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  background: linear-gradient(45deg, #6F1A3F, #C82B3F);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: #ccc;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
`;

const FeatureItem = styled.li`
  color: #ccc;
  margin-bottom: 0.5rem;
  position: relative;
  padding-left: 1.5rem;

  &::before {
    content: '▶';
    position: absolute;
    left: 0;
    color: #C82B3F;
    font-size: 0.8rem;
  }
`;

const Slide5 = () => {
  const services = [
    {
      icon: '📈',
      title: 'Digital Marketing Strategy',
      description: 'Comprehensive digital marketing campaigns',
      features: [
        'Social media strategy',
        'Content marketing & SEO',
        'Performance-driven campaigns',
        'Marketing optimization'
      ]
    },
    {
      icon: '🎨',
      title: 'Brand Development',
      description: 'Complete brand identity and positioning',
      features: [
        'Brand strategy & positioning',
        'Visual identity design',
        'Brand guidelines',
        'Market differentiation'
      ]
    },
    {
      icon: '👥',
      title: 'Influencer & Partner Marketing',
      description: 'Strategic partnerships and influencer campaigns',
      features: [
        'Influencer marketing campaigns',
        'Partner relationship management',
        'Community engagement',
        'Social media strategies'
      ]
    },
    {
      icon: '📢',
      title: 'Public Relations',
      description: 'PR strategy and reputation management',
      features: [
        'PR strategy & execution',
        'Media relations',
        'Crisis communication',
        'Reputation management'
      ]
    }
  ];

  return (
    <SlideContainer>
      <SlideTitle
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        Digital Marketing & Brand Solutions
      </SlideTitle>

      <ServicesGrid>
        {services.map((service, index) => (
          <ServiceCard
            key={index}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
          >
            <ServiceIcon>{service.icon}</ServiceIcon>
            <ServiceTitle>{service.title}</ServiceTitle>
            <ServiceDescription>{service.description}</ServiceDescription>
            <ServiceFeatures>
              {service.features.map((feature, featureIndex) => (
                <FeatureItem key={featureIndex}>{feature}</FeatureItem>
              ))}
            </ServiceFeatures>
          </ServiceCard>
        ))}
      </ServicesGrid>
    </SlideContainer>
  );
};

export default Slide5;
